import { useCallback, useEffect, useRef, useState } from 'react';

interface PingPongAutoplayOptions {
  min: number;
  max: number;
  delay?: number;
  period?: number;
  onChange?: (value: number) => void;
}

function usePingPongAutoplay(options: PingPongAutoplayOptions) {
  const delay = options.delay ?? 5000;
  const period = options.period ?? 8000;
  const [value, setValue] = useState(options.min);
  const valueRef = useRef(value);
  const directionRef = useRef(1);
  const lastInteractionRef = useRef(Date.now());
  const lastFrameRef = useRef<number>();
  const onChangeRef = useRef(options.onChange);

  useEffect(() => {
    onChangeRef.current = options.onChange;
  }, [options.onChange]);

  const updateValue = useCallback((nextValue: number, userInteraction = false) => {
    const clampedValue = Math.min(Math.max(nextValue, options.min), options.max);
    valueRef.current = clampedValue;
    setValue(clampedValue);
    if (userInteraction) lastInteractionRef.current = Date.now();
    if (onChangeRef.current) onChangeRef.current(clampedValue);
  }, [options.min, options.max]);

  useEffect(() => {
    updateValue(valueRef.current, false);
  }, [options.min, options.max, updateValue]);

  useEffect(() => {
    let animationFrame: number;

    function tick(timestamp: number) {
      if (lastFrameRef.current === undefined) lastFrameRef.current = timestamp;
      const delta = timestamp - lastFrameRef.current;
      lastFrameRef.current = timestamp;

      const range = options.max - options.min;
      if (range > 0 && Date.now() - lastInteractionRef.current >= delay) {
        let nextValue = valueRef.current + directionRef.current * range * (delta / period);

        if (nextValue >= options.max) {
          nextValue = options.max - (nextValue - options.max);
          directionRef.current = -1;
        }

        if (nextValue <= options.min) {
          nextValue = options.min + (options.min - nextValue);
          directionRef.current = 1;
        }

        updateValue(nextValue, false);
      }

      animationFrame = requestAnimationFrame(tick);
    }

    animationFrame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationFrame);
  }, [delay, options.min, options.max, period, updateValue]);

  const handleChange = useCallback((event: any, newValue: number | number[]) => {
    updateValue(newValue as number, true);
  }, [updateValue]);

  return {
    value,
    handleChange,
  };
}

export default usePingPongAutoplay;
